// Bandeja de tickets (artboard a adaptado a Semáforo, h1 en celular): chips
// por estado y semáforo, búsqueda con conteo, tabla en escritorio y tarjetas
// en celular. Lo urgente arriba.
import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { EsqueletoTabla, Input, PuntoNaranja, TituloPagina, Vacio, cn } from "@iavmx/ui";
import { useBandeja, usePersona, type Ticket } from "./datos";
import { BotonNuevoTicket, ErrorCarga, PillEstado, Segmento, SemaforoTicket } from "./comunes";
import { ETIQUETA_PRIORIDAD, estadoLabel, fechaCorta, folio, relativo } from "./formato";

type Alcance = "abiertos" | "cerrados" | "todos";
type FiltroSem = "vencido" | "ambar" | "verde" | undefined;

function Chip({ activo, onClick, children, punto }: { activo: boolean; onClick: () => void; children: React.ReactNode; punto?: "rojo" | "ambar" | "verde" }) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onClick}
      className={cn(
        "inline-flex h-9 items-center gap-2 border px-3 font-ui text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        activo ? "border-tinta bg-tinta text-tinta-inversa" : "border-linea text-tinta-2 hover:bg-fondo-suave",
      )}
    >
      {punto && <span className={cn("size-2 rounded-full", punto === "rojo" ? "bg-rojo" : punto === "ambar" ? "bg-ambar" : "bg-verde")} aria-hidden="true" />}
      {children}
    </button>
  );
}

export function FilaTicketMovil({ t, ahora }: { t: Ticket; ahora: Date }) {
  return (
    <li className={cn("border-b border-linea", t.vencido && "bg-rojo/5")}>
      <Link to="/tickets/$id" params={{ id: t.id }} className="flex min-h-11 flex-col gap-1.5 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <div className="flex items-center justify-between gap-2">
          <SemaforoTicket ticket={t} ahora={ahora} />
          <PillEstado estado={t.estado} />
        </div>
        <strong className="font-ui text-base">{t.titulo}</strong>
        <span className="font-ui text-sm text-tinta-2">
          {folio(t.folio)} · {t.categoria?.nombre ?? "sin categoría"} · {t.asignada?.nombre ?? "sin asignar"} · pidió {t.solicitante?.nombre ?? "—"} · vence {fechaCorta(t.venceEn)}
        </span>
      </Link>
    </li>
  );
}

export function TablaTickets({ tickets, ahora }: { tickets: Ticket[]; ahora: Date }) {
  return (
    <>
      <ul className="flex flex-col md:hidden">
        {tickets.map((t) => (
          <FilaTicketMovil key={t.id} t={t} ahora={ahora} />
        ))}
      </ul>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[760px] font-ui text-sm">
          <thead>
            <tr className="border-b border-tinta text-left text-[11px] font-semibold uppercase tracking-wide text-tinta-tenue">
              <th className="w-44 py-2 pr-3 font-semibold">Semáforo</th>
              <th className="py-2 pr-3 font-semibold">Ticket</th>
              <th className="w-40 py-2 pr-3 font-semibold">Pidió</th>
              <th className="w-44 py-2 pr-3 font-semibold">Responsable</th>
              <th className="w-32 py-2 pr-3 font-semibold">Fecha límite</th>
              <th className="w-28 py-2 font-semibold">Estado</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr key={t.id} className={cn("border-b border-linea align-top hover:bg-fondo-suave", t.vencido && "bg-rojo/5")}>
                <td className="py-3 pr-3">
                  <SemaforoTicket ticket={t} ahora={ahora} />
                </td>
                <td className="py-3 pr-3">
                  <Link to="/tickets/$id" params={{ id: t.id }} className="font-semibold hover:underline">
                    {t.titulo}
                  </Link>
                  <div className="text-xs text-tinta-tenue">
                    {folio(t.folio)} · {t.categoria?.nombre ?? "sin categoría"} · prioridad {ETIQUETA_PRIORIDAD[t.prioridad]?.toLowerCase()}
                    {t.origenIa && " · desde un mensaje"}
                  </div>
                </td>
                <td className="py-3 pr-3">
                  {t.areaSolicitante?.nombre ?? "—"}
                  <div className="text-xs text-tinta-tenue">{t.solicitante?.nombre}</div>
                </td>
                <td className="py-3 pr-3">
                  {t.asignada?.nombre ?? <span className="text-tinta-tenue">Sin asignar</span>}
                  <div className="text-xs text-tinta-tenue">{t.areaAsignada?.nombre}</div>
                </td>
                <td className="py-3 pr-3 tabular-nums">
                  {fechaCorta(t.venceEn)}
                  <div className="text-xs text-tinta-tenue">{relativo(t.venceEn, ahora)}</div>
                </td>
                <td className="py-3">
                  <PillEstado estado={t.estado} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function Bandeja() {
  const [alcance, setAlcance] = React.useState<Alcance>("abiertos");
  const [estado, setEstado] = React.useState<string | undefined>();
  const [sem, setSem] = React.useState<FiltroSem>();
  const [mios, setMios] = React.useState(false);
  const [busqueda, setBusqueda] = React.useState("");
  const [q, setQ] = React.useState("");
  React.useEffect(() => {
    const t = setTimeout(() => setQ(busqueda.trim()), 250);
    return () => clearTimeout(t);
  }, [busqueda]);
  const yo = usePersona();
  const b = useBandeja({ alcance, estado, semaforo: sem, mios: mios ? "1" : undefined, q: q || undefined });
  const d = b.data;
  const ahora = d ? new Date(d.ahora) : new Date();
  const c = d?.conteos;
  const hayFiltro = Boolean(estado || sem || mios || q);
  const estadosChips = alcance === "cerrados" ? ["entregada", "cerrada", "cancelada"] : alcance === "todos" ? ["nueva", "asignada", "en_proceso", "en_revision", "entregada", "cerrada"] : ["nueva", "asignada", "en_proceso", "en_revision"];
  return (
    <>
      <TituloPagina
        descripcion={
          d
            ? `${fechaCorta(ahora)} · ${c!.total} ${alcance === "abiertos" ? (c!.total === 1 ? "abierto" : "abiertos") : alcance === "cerrados" ? (c!.total === 1 ? "cerrado" : "cerrados") : "en total"}${c!.porSemaforo.vencido > 0 ? `, ${c!.porSemaforo.vencido} ${c!.porSemaforo.vencido === 1 ? "vencido" : "vencidos"}` : ""}${yo.data?.veTodo ? "" : " · ves los de tu área y los tuyos"}`
            : "Cargando tickets…"
        }
        accion={
          <div className="flex w-full flex-wrap items-center gap-3 md:w-auto">
            <label className="relative w-full md:w-64">
              <span className="sr-only">Buscar ticket, folio o persona</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-tinta-tenue" aria-hidden="true" />
              <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar ticket, folio o persona" className="pl-9 text-base md:text-sm" />
            </label>
            <BotonNuevoTicket className="w-full md:w-auto" />
          </div>
        }
      >
        Tickets
        <PuntoNaranja />
      </TituloPagina>
      <div className="mb-4 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Segmento
            etiqueta="Alcance"
            valor={alcance}
            onChange={(v) => {
              setAlcance(v);
              setEstado(undefined);
              setSem(undefined);
            }}
            opciones={[
              { valor: "abiertos", texto: "Abiertos" },
              { valor: "cerrados", texto: "Cerrados" },
              { valor: "todos", texto: "Todos" },
            ]}
          />
          <Chip activo={mios} onClick={() => setMios((v) => !v)}>
            Míos
          </Chip>
        </div>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filtros">
          <Chip activo={!estado} onClick={() => setEstado(undefined)}>
            Todos {c && <span className="font-bold tabular-nums">{c.total}</span>}
          </Chip>
          {estadosChips.map((e) => (
            <Chip key={e} activo={estado === e} onClick={() => setEstado(estado === e ? undefined : e)}>
              {estadoLabel(e)} {c && <span className="font-bold tabular-nums">{c.porEstado[e as keyof typeof c.porEstado]}</span>}
            </Chip>
          ))}
          {alcance !== "cerrados" && (
            <>
              <span className="mx-1 hidden h-5 w-px bg-linea sm:block" aria-hidden="true" />
              <Chip punto="rojo" activo={sem === "vencido"} onClick={() => setSem(sem === "vencido" ? undefined : "vencido")}>
                Vencidos {c && <span className="font-bold tabular-nums">{c.porSemaforo.vencido}</span>}
              </Chip>
              <Chip punto="ambar" activo={sem === "ambar"} onClick={() => setSem(sem === "ambar" ? undefined : "ambar")}>
                Por vencer {c && <span className="font-bold tabular-nums">{c.porSemaforo.ambar}</span>}
              </Chip>
              <Chip punto="verde" activo={sem === "verde"} onClick={() => setSem(sem === "verde" ? undefined : "verde")}>
                A tiempo {c && <span className="font-bold tabular-nums">{c.porSemaforo.verde}</span>}
              </Chip>
            </>
          )}
        </div>
      </div>
      {b.isLoading ? (
        <EsqueletoTabla filas={6} columnas={5} />
      ) : b.error ? (
        <ErrorCarga error={b.error} />
      ) : d!.tickets.length === 0 ? (
        hayFiltro ? (
          <Vacio titulo="Sin coincidencias" texto="Prueba con otra palabra o quita un filtro." />
        ) : (
          <Vacio titulo={alcance === "abiertos" ? "Sin tickets abiertos" : "Nada por aquí"} texto="Pega un mensaje de WhatsApp o correo y la interfaz propone el ticket; tú confirmas." accion={<BotonNuevoTicket />} />
        )
      ) : (
        <>
          <TablaTickets tickets={d!.tickets} ahora={ahora} />
          <p className="mt-3 font-ui text-xs text-tinta-tenue">
            Mostrando {d!.tickets.length} de {c!.total}. Orden: vencidos, por vencer, a tiempo; dentro, por fecha límite.
          </p>
        </>
      )}
    </>
  );
}
